import { LanguageContext } from "@/app/[lang]/client/language-context";
import { useContext } from "react";

export const getLanguage = () => {
  try {
    const language = localStorage.getItem("language");
    if (language) {
      return language;
    }
    return "en";
  } catch (error) {
    console.error("Error retrieving language from local storage:", error);
  }
};

export const setLanguage = (language: string) => {
  try {
    localStorage.setItem("language", language);
  } catch (error) {
    console.error("Error saving language to local storage:", error);
  }
};

export const ifArabic = (p1: any, p2: any) => {
  const lang = useContext(LanguageContext);

  if (lang?.language === "ar") {
    return p1;
  } else {
    return p2;
  }
};

/**
 * Creates a localized path by prefixing with language code (Arabic only)
 * English paths have no prefix
 * @param path - The path to localize
 * @param lang - The language code (e.g., 'en', 'ar')
 * @returns Localized path with language prefix for Arabic, no prefix for English
 */
export const getLocalizedPath = (path: string, lang: string = "en"): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  // Don't add language prefix for assets, api routes, or already localized paths
  if (
    cleanPath.startsWith("assets/") ||
    cleanPath.startsWith("api/") ||
    cleanPath.startsWith("_next/") ||
    cleanPath.startsWith("ar/")
  ) {
    return `/${cleanPath}`;
  }

  // For English, return path without language prefix
  if (lang === "en") {
    // Handle root path
    if (cleanPath === "" || cleanPath === "/") {
      return "/";
    }
    return `/${cleanPath}`;
  }

  // For Arabic, add /ar prefix
  if (lang === "ar") {
    // Handle root path
    if (cleanPath === "" || cleanPath === "/") {
      return "/ar";
    }
    return `/ar/${cleanPath}`;
  }

  // Fallback to English pattern for unknown languages
  return `/${cleanPath}`;
};

/**
 * Switches the current URL to use a different language
 * @param currentPath - The current pathname
 * @param newLang - The new language code to switch to
 * @returns The new URL with the updated language
 */
export const switchLanguageInUrl = (
  currentPath: string,
  newLang: string
): string => {
  // Remove leading slash
  const cleanPath = currentPath.startsWith("/")
    ? currentPath.slice(1)
    : currentPath;

  // Check if path already has a language prefix
  if (cleanPath.startsWith("ar")) {
    // Replace the existing language prefix
    const pathWithoutLang = cleanPath.substring(2); // Remove first 3 characters (e.g., "en/")
    return getLocalizedPath(pathWithoutLang, newLang);
  }
  // If no language prefix exists, add one
  return getLocalizedPath(cleanPath, newLang);
};

/**
 * Detects the current language from the URL pathname
 * @param pathname - The current pathname
 * @returns The detected language ('en' or 'ar')
 */
export const detectLanguageFromUrl = (pathname: string): string => {
  // Check if path starts with /ar
  if (pathname.startsWith("/ar/") || pathname === "/ar") {
    return "ar";
  }
  // Default to English for all other paths
  return "en";
};
